import type { HeartRateEvent } from '@model/DeviceConfiguration';
import {
  eventToRelativeSecond,
  getSessionTrackOriginTimestamp,
} from '../../../shared/hr-data';
import type { CustomTimeSegment } from '../types';
import { formatSecondLabel } from './trendZoom';

export const buildCustomSegmentValueMap = (
  fullTrackMap: Map<number, number>,
  startSecond: number,
  endSecond: number
): Map<number, number> => {
  const entries = [...fullTrackMap.entries()]
    .filter(([second]) => second >= startSecond && second <= endSecond)
    .map(([second, value]) => [second - startSecond, value] as const);

  return new Map(entries);
};

export const filterEventsByCustomSegment = (
  events: HeartRateEvent[],
  startSecond: number,
  endSecond: number
): HeartRateEvent[] => {
  if (events.length === 0) return [];

  const sorted = [...events].sort(
    (left, right) => left.timestamp - right.timestamp
  );
  const originTimestamp = getSessionTrackOriginTimestamp(sorted);
  if (originTimestamp == null) return [];

  return sorted.filter(event => {
    const second = eventToRelativeSecond(event.timestamp, originTimestamp);
    return second >= startSecond && second <= endSecond;
  });
};

export const validateCustomSegment = (
  startSecond: number,
  endSecond: number,
  maxSecond: number
): string | null => {
  if (!Number.isFinite(startSecond) || !Number.isFinite(endSecond)) {
    return 'Inserisci inizio e fine validi.';
  }
  if (startSecond < 0 || endSecond < 0) {
    return 'I timestamp devono essere non negativi.';
  }
  if (startSecond >= endSecond) {
    return 'Il timestamp di inizio deve essere precedente alla fine.';
  }
  if (endSecond > maxSecond) {
    return 'Il timestamp di fine supera la durata disponibile.';
  }
  return null;
};

export const formatSegmentTabLabel = (segment: CustomTimeSegment) =>
  segment.label.trim() ||
  `${formatSecondLabel(segment.startSecond)}–${formatSecondLabel(segment.endSecond)}`;

export const createCustomSegmentId = () =>
  `segment-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
